import { useEffect, useState } from 'react';

import { useShowResponseStatus } from '@/shared';

import {
  useGetH2HPaymentCountsQuery,
  useGetH2HPaymentsQuery,
} from '../store/h2h-payments-api';

import type {
  TGetH2HPayments,
  TH2HPaymentFilters,
  TH2HPaymentQueueTab,
} from '../store/types';

const initialParams: TGetH2HPayments = {
  tab: 'all',
  page: 1,
  pageSize: 30,
};

const emptyCounts = {
  actionRequired: 0,
  awaitingProvider: 0,
  discrepancies: 0,
  completed: 0,
  rejected: 0,
  all: 0,
};

export const useGetH2HPayments = () => {
  const [params, setParams] = useState<TGetH2HPayments>(initialParams);
  const filters: TH2HPaymentFilters = {
    id: params.id,
    createdFrom: params.createdFrom,
    createdTo: params.createdTo,
    payerId: params.payerId,
    shopId: params.shopId,
    methodId: params.methodId,
    remoteId: params.remoteId,
    credentials: params.credentials,
  };
  const paymentsQuery = useGetH2HPaymentsQuery(params, { pollingInterval: 60000 });
  const countsQuery = useGetH2HPaymentCountsQuery(filters, { pollingInterval: 60000 });

  useEffect(() => {
    const itemsCount = paymentsQuery.currentData?.count;
    if (itemsCount === undefined) return;

    const totalPages = Math.max(1, Math.ceil(itemsCount / (params.pageSize ?? 30)));
    if ((params.page ?? 1) > totalPages) {
      setParams(current => ({ ...current, page: totalPages }));
    }
  }, [params.page, params.pageSize, paymentsQuery.currentData?.count]);

  useShowResponseStatus({
    isError: paymentsQuery.isError || countsQuery.isError,
    errorMassage: 'Не удалось загрузить H2H-очередь',
  });

  const handleTabChange = (nextTab: TH2HPaymentQueueTab) => {
    setParams(current => ({ ...current, tab: nextTab, page: 1 }));
  };

  const handleFilterChange = (nextFilters: TH2HPaymentFilters) => {
    setParams(current => ({
      ...initialParams,
      ...nextFilters,
      tab: current.tab,
    }));
  };

  const handleFilterReset = () => {
    setParams(current => ({ ...initialParams, tab: current.tab }));
  };

  const handlePaginationChange = (page: number) => {
    setParams(current => ({ ...current, page }));
  };

  return {
    params,
    items: paymentsQuery.currentData?.items ?? [],
    itemsCount: paymentsQuery.currentData?.count ?? 0,
    counts: countsQuery.currentData ?? emptyCounts,
    isLoading: paymentsQuery.currentData === undefined || countsQuery.currentData === undefined,
    handleTabChange,
    handleFilterChange,
    handleFilterReset,
    handlePaginationChange,
  };
};
